import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
  IWixStyleFont,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';
import { IChallengeStyles } from './Settings/challengeStyles/challengeStyles.types';
import { challengeStyles as mobileStyles } from './Settings.mobile/challengeStyles';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import {
  AlignmentMap,
  TextAlignment,
} from './Settings/challengeSettings/challengeSettings.types';
import { debug } from '../../utils/queryString';

const alignmentEntries = Object.entries(TextAlignment);

export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const booleans = styleParams.booleans;
  const cssVars = Object.keys(booleans).reduce((result, key) => {
    result[key] = booleans[key] ? 'initial' : 'none';
    return result;
  }, {} as Record<string, string>);
  const numbers = styleParams.numbers;

  if (cssVars.buttonHoverUnderlineStyle === 'initial') {
    cssVars.buttonHoverUnderlineStyle = 'underline';
  }

  cssVars.opgListTitleAlignment =
    alignmentEntries[numbers.opgListTitleAlignment][1];
  const opgTextAlignment = alignmentEntries[numbers.opgTextAlignment][1];
  cssVars.opgTextAlignment = opgTextAlignment;
  cssVars.opgDividerAlignment = AlignmentMap[opgTextAlignment];
  debug('customCssVars', cssVars);
  return cssVars;
};

export const fontValueFix = (fontFn, textPresets): IWixStyleFont => {
  const { value: _, ...font } = fontFn({ textPresets });

  return {
    ...font,
  } as IWixStyleFont;
};

export const programListStylesParams = createStylesParams<IChallengeStyles>({
  appBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-1', opacity: 0 },
      ai: { description: 'Background color of the program list section.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
  },
  badgesBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: { description: 'Background color of program card badges.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  badgesTextColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: { description: 'Text color of program card badges.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description:
          'Background color of primary action buttons on program cards.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  buttonFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: {
        description: 'Text color of primary action buttons on program cards.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: {
        description: 'Font used by primary action buttons on program cards.',
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  buttonBorderColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-2' },
      ai: {
        description: 'Border color of primary action buttons on program cards.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
  },
  buttonBorderWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description: 'Border width of primary action buttons on program cards.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  buttonCornerRadius: {
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Corner radius of primary action buttons on program cards.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  buttonHoverBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.7 },
      ai: {
        description: 'Background color of primary action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  buttonHoverBorderColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-8', opacity: 0.7 },
      ai: {
        description: 'Border color of primary action buttons when hovered.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  buttonHoverTextColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: { description: 'Text color of primary action buttons when hovered.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonHoverUnderlineStyle: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'Whether primary action button text is underlined when hovered.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue: ({ experiments }) => {
      return !!experiments?.enabled('specs.programs.ButtonsUnderlineStyle');
    },
  },
  cardSidesPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 32 },
      ai: { description: 'Horizontal padding inside each program card.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: () => 32,
  },
  cardTopBottomPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 32 },
      ai: { description: 'Vertical padding inside each program card.' },
    },
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: () => 32,
  },
  cardBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: { description: 'Background color of each program card.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  cardBorderColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.2 },
      ai: { description: 'Border color of each program card.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
  },
  cardBorderWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Border width of each program card.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  cardCornerRadius: {
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Corner radius of each program card.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  titleMaxWidth: {
    metadata: {
      type: StyleType.width,
      defaultValue: { type: 'static', value: 100 },
      ai: { description: 'Maximum width of titles on program cards.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  },
  dividerColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.3 },
      ai: { description: 'Color of dividers on program cards.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
  },
  dividerLength: {
    metadata: {
      type: StyleType.width,
      defaultValue: { type: 'static', value: 12.5 },
      ai: { description: 'Length of dividers on program cards.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 12.5,
  },
  dividerWidth: {
    metadata: {
      type: StyleType.height,
      defaultValue: { type: 'static', value: 1 },
      ai: { description: 'Width of dividers on program cards.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  nameFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 24 },
      ai: { description: 'Font used for each program name.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 24,
        }),
        textPresets,
      );
    },
  },
  nameFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of each program name.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  participantsAndDurationFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: {
        description: 'Font used for participant count and program duration.',
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  participantsAndDurationFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: {
        description:
          'Text color used for participant count and program duration.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  priceFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for each program price.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  priceFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of each program price.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  imageOpacity: {
    metadata: {
      type: StyleType.opacity,
      defaultValue: { type: 'static', value: 100 },
      ai: { description: 'Opacity of program card images.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  },
  imageBackgroundColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-1' },
      ai: { description: 'Background color behind program card images.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  imageBorderColor: {
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Border color of program card images.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  imageBorderWidth: {
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Border width of program card images.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  imageCornerRadius: {
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Corner radius of program card images.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  imageInnerPadding: {
    metadata: {
      type: StyleType.padding,
      defaultValue: { type: 'static', value: 0 },
      ai: { description: 'Space between a program card image and its border.' },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  sideBySideProportions: {
    metadata: {
      type: StyleType.width,
      defaultValue: { type: 'static', value: 50 },
      ai: {
        description:
          'Relative width of the image when cards use a side-by-side layout.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue: () => 50,
  },
  titleFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Page-title', size: 32 },
      ai: { description: 'Font used for the program list title.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 32,
        }),
        textPresets,
      );
    },
  },
  titleFontColor: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of the program list title.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  categoriesTabsFontTabs: {
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      ai: { description: 'Font used for category tab labels.' },
    },
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  categoriesTabsFontColorTabs: {
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-5' },
      ai: { description: 'Text color of category tab labels.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  categoriesTabsDividerColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.2 },
      ai: { description: 'Color of the divider below category tabs.' },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2), // 20%
  },
  categoriesTabsSelectedUnderlineColor: {
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-8' },
      ai: {
        description: 'Color of the underline below the selected category tab.',
      },
    },
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  opgDisplayImage: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show images on program cards.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayDuration: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show program duration on program cards.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayParticipants: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: {
        description: 'Whether to show the participant count on program cards.',
      },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayDivider: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show dividers on program cards.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayPrice: {
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: { description: 'Whether to show prices on program cards.' },
    },
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgCardSpacing: {
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 32 },
      ai: { description: 'Space between program cards in a grid.' },
    },
    type: StyleParamType.Number,
    getDefaultValue() {
      return 32;
    },
  },
  opgSideCardsVerticalSpacing: {
    metadata: {
      type: StyleType.gap,
      defaultValue: { type: 'static', value: 32 },
      ai: {
        description:
          'Vertical space between program cards in a side-by-side layout.',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue() {
      return 32;
    },
  },
  opgItemsPerRow: {
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: 3 },
      ai: { description: 'Number of program cards displayed in each row.' },
    },
    type: StyleParamType.Number,
    getDefaultValue() {
      return 3;
    },
  },
  opgListTitleAlignment: {
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Horizontal alignment of the program list title: left (0), center (1), or right (2).',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue() {
      return 0;
    },
  },
  opgTextAlignment: {
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Horizontal alignment of text inside program cards: left (0), center (1), or right (2).',
      },
    },
    type: StyleParamType.Number,
    getDefaultValue() {
      return 0;
    },
  },
  ...mobileStyles,
});

export default programListStylesParams;
export type IChallengeStylesKeys = keyof IChallengeStyles;
export type IChallengeListStylesVars = `--${IChallengeStylesKeys}`;
